import type { IChat, IMessage } from '@/lib/types/chat';
import { fetchPersisted } from '@/lib/stores/chat-store/chat-fetch';
import { draftMatchesScope } from '@/lib/utils/new-chat-route';

/**
 * Draft chats (KAN-1266): a chat that exists only in the store until its
 * first message creates the session. The building blocks the drafts slice
 * puts together; the bindings by thread id are the one piece of state here.
 */

// A persisted session id is always a UUID. Every placeholder a draft chat can
// carry is not: our own ``draft-…`` ids and assistant-ui's ``__LOCALID_…``
// thread ids. Used by the drift guard in ``ensureSessionForCurrentChat``.
export const PERSISTED_SESSION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const generateDraftId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `draft-${crypto.randomUUID()}`;
  }
  return `draft-${Date.now()}`;
};

// Bound drafts by the thread id they were opened under. The drift guard in
// ``ensureSessionForCurrentChat`` rebuilds a draft the store has moved away
// from; without this it would come back as a plain chat and the first message
// would land outside the agent or project it was written in.
export const boundDraftsByThread = new Map<string, IChat>();

/**
 * The session each draft became, by the thread id the draft was opened under
 * (KAN-1825). An upload creates the session before the first message, and
 * until that message goes out the runtime hands the same thread, its composer
 * and the uploaded file in it, out again as the next new chat. Opening that
 * thread as a fresh draft created a second session on the send and left the
 * first one behind, empty. A thread whose first message went out is never
 * handed out as a new chat again, so its entry is simply never asked for.
 */
const promotedDraftsByThread = new Map<string, IChat>();

/** Note that the draft opened under ``threadId`` became the session ``chat``. */
export const rememberPromotedDraft = (threadId: string, chat: IChat): void => {
  promotedDraftsByThread.set(threadId, chat);
};

/**
 * The plain chat the draft opened under ``threadId`` became, if any. An
 * agent's, project's or guided draft is opened through its entry point, whose
 * freshly prepared draft wins (``createNewChat``).
 */
export const promotedPlainDraft = (threadId: string): IChat | undefined => {
  const chat = promotedDraftsByThread.get(threadId);
  return chat && draftMatchesScope(chat, {}) ? chat : undefined;
};

/** Forget a session that is gone: its thread opens as a fresh draft again. */
export const forgetPromotedDraft = (sessionId: string): void => {
  for (const [threadId, chat] of promotedDraftsByThread) {
    if (chat.id === sessionId) promotedDraftsByThread.delete(threadId);
  }
};

/**
 * Forget every binding by thread id. On a workspace switch: a draft or a
 * session of the workspace left behind must not come back in the next one.
 */
export const forgetDraftThreads = (): void => {
  boundDraftsByThread.clear();
  promotedDraftsByThread.clear();
};

/**
 * Whether ``chat`` is the one a composer bound to ``boundChatId`` writes into:
 * that draft, or the session it became. The composer stays bound to the
 * draft's thread id after the promotion, so without the second half every
 * further photo picked into an unsent chat created one more empty chat, and
 * the composer kept the photos uploaded into the earlier ones (KAN-1621).
 */
export const isBoundChat = (chat: IChat | null, boundChatId: string): boolean =>
  chat?.id === boundChatId || chat?.promotedFromDraftId === boundChatId;

/**
 * The greeting a session was just created with, as the API stored it. It
 * goes into the thread with its database id before the first message does
 * (``importGreetingOfPromotedDraft``): the message is stored under it, and
 * editing that message later branches off the greeting instead of chaining
 * onto an answer.
 */
export const loadGreeting = async (
  sessionId: string,
  text: string,
  model: string | undefined
): Promise<IMessage[]> => {
  try {
    const stored = await fetchPersisted(sessionId, 10);
    if (stored.length > 0) return stored;
  } catch {
    // Fall through: the send must not fail over the greeting.
  }
  // Not a UUID on purpose: the API maps a parent it cannot parse onto the
  // chat's last answer, which is this greeting.
  return [
    {
      id: `greeting-${sessionId}`,
      role: 'assistant',
      content: text,
      timestamp: new Date(),
      parentId: null,
      modelId: model ?? null,
    },
  ];
};

/**
 * A prepared draft's model, selected right away. The agent and project frames
 * draw their model picker as soon as the address changes, a moment before the
 * draft is opened; selecting it only then showed the previous model in
 * between.
 */
export const draftModelSelection = (
  draft: IChat
): { selectedModel?: string; modelSelectedAt?: number } =>
  draft.model ? { selectedModel: draft.model, modelSelectedAt: 0 } : {};

export const createDraftChat = (model?: string): IChat => ({
  id: generateDraftId(),
  // Empty placeholder — the display layer localizes it via t('untitledChat').
  // The backend persists a locale-aware title once the chat is created.
  title: '',
  messages: [],
  createdAt: new Date(),
  updatedAt: new Date(),
  model,
  isDraft: true,
});
